import { clearBrowserStorage } from './browserStorage';
import { updateAnalytics } from '@services/analytics';
import { createPresentation } from '@services/presentation';
import { ANALYTICS_EVENTS, PAGE_URLS, PresentationType, Profile } from '@constants';
import { LoginInfo } from '@pages/Login/ClientLogin';
import { createAnalyticsEventBody } from './analytics';


const { loginDateAndTime, slidesPresentationEvent, documentsPresentationEvent, slidesPresentationStarted } = ANALYTICS_EVENTS;
const { TESTING, ADMIN, CUSTOMER_SERVICE } = Profile;
const { TESTING_PAGE } = PAGE_URLS;
const { DOCUMENTS_PRESENTATION, SLIDES_PRESENTATION, CUSTOMER_SERVICE_PRESENTATION } = PresentationType;

type RedirectState = {
    leadId?: number,
    profile?: string,
    presentationId?: number,
    client?: {
        leadId: number,
        email: string
    }
}

export const handleLoginSuccess = async (loginInfo: LoginInfo, history) => {
    const { profile, presentationType, salesRepId } = loginInfo;
    
    if (profile !== ADMIN) {
        clearBrowserStorage();
    }

    const presentationId = await createPresentationWithAnalytics(profile, presentationType, salesRepId);

    redirectBasedOnLoginInfo(loginInfo, history, presentationId);
};

async function createPresentationWithAnalytics(profile: string, presentationType: string, salesRepId: number) {
    if (profile !== TESTING && profile !== ADMIN) {
        try {
            const isCsProfile = profile === CUSTOMER_SERVICE;
            const presentationModelEventType = isCsProfile ? CUSTOMER_SERVICE_PRESENTATION : presentationType;
            // CS presentations start on login. Other presentations start only when client connects
            const presentationStart = isCsProfile ? new Date() : null;
            
            const { presentationId } = await createPresentation({ presentationType: presentationModelEventType, presentationStart, salesRepId });
    
            if (isCsProfile) {
                await updateAnalytics(createAnalyticsEventBody(presentationId, loginDateAndTime, slidesPresentationEvent, slidesPresentationStarted));
            } else {
                // In the future the x_presentation_started analytic will no longer be necessary as presentation type will be stored on the presentation model
                // Leaving in place for now so that existing analytics queries continue to work
                const isSlidesPresentation = presentationType === SLIDES_PRESENTATION;
                const salesPresentationEventType = isSlidesPresentation ? slidesPresentationEvent : documentsPresentationEvent;
                await updateAnalytics(createAnalyticsEventBody(presentationId, loginDateAndTime, salesPresentationEventType));
            }
                
            return presentationId;
        } catch (err) {
            console.error('Presentation was not created', err);
            return null;
        }
    }
}

function redirectBasedOnLoginInfo(loginInfo, history, presentationId) {
    let pathname: string = getPathName(loginInfo);
    let redirectState: RedirectState = getLocationState(loginInfo, presentationId);

    redirectWithState(history, pathname, redirectState);
}

function getPathName(loginInfo) {
    const { leadId, profile, presentationType } = loginInfo;

    if (profile === CUSTOMER_SERVICE) {
        return `/customer-service/${leadId}`;
    } else if (profile === TESTING) {
       return TESTING_PAGE;
    } else if (presentationType === DOCUMENTS_PRESENTATION) {
        return `/documents/${leadId}`
    } else {
        return `/slides/${leadId}`;
    }
}

function getLocationState(loginInfo, presentationId) {
    const { email, leadId, profile } = loginInfo;
    const numericLeadId = parseInt(leadId);

    if (profile === TESTING) {
        return ({
            client: { 
                email, leadId: numericLeadId
            }
        })
    } else {
        return ({
            leadId: numericLeadId,
            profile,
            presentationId
        })
    }
}

function redirectWithState(history, pathname, state: RedirectState) {
    history.push({
        pathname, state
    });
}
